/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    IAnalyticsPolicy,
    IFullClientLogs,
    IMetricsRealTimeUpdate,
} from 'generated-types';

import { AnalyticsPolicy } from 'object-types';

import {
    MessageItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { ClientLogFilterConfigItem } from './client-log-filter.config-item.factory';
import { ClientInsightsSamplingConfigItem } from './client-insights-sampling.config-item.factory';

/**
 * Partial Type of Analytics Policy object.
 */
type TAnalyticsPolicyPartial = Omit<IAnalyticsPolicy,
'metrics_realtime_update' |
'full_client_logs' |
'client_log_filters' |
'client_insights_sampling'
>;

/**
 * Type of Analytics Policy Config object.
 */
interface IAnalyticsPolicyConfig extends TAnalyticsPolicyPartial {
    metrics_realtime_update: MessageItem<IMetricsRealTimeUpdate>;
    full_client_logs: MessageItem<IFullClientLogs>;
    client_log_filters: RepeatedMessageItem<ClientLogFilterConfigItem>;
    client_insights_sampling: ClientInsightsSamplingConfigItem
}

/**
 * @description
 *
 *   Analytics Policy Message Item.
 *
 * @author Sarthak kapoor
 */
export class AnalyticsPolicyConfigItem extends
    withEditChildMessageItemMixin(MessageItem)<IAnalyticsPolicyConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: AnalyticsPolicy,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Remove the selected client log filter.
     */
    public removeSelectedClientLogFilter(filter: ClientLogFilterConfigItem): void {
        this.config.client_log_filters.removeByMessageItem(filter);
    }

    /**
     * Open the Client Log Filter modal to add a new filter.
     */
    public addClientLogFilter(): void {
        this.addChildMessageItem({
            field: 'client_log_filters',
        });
    }

    /**
     * Open the Client Log Filter modal in the edit mode.
     */
    public editClientLogFilter(
        clientLogFilter: ClientLogFilterConfigItem,
    ): void {
        this.editChildMessageItem({
            field: 'client_log_filters',
            messageItem: clientLogFilter,
            modalBindings: { isEditing: true },
        });
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'metrics_realtime_update',
            'full_client_logs',
            'client_log_filters',
            'client_insights_sampling',
        ];
    }
}
